/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback } from 'react';
import { Button, Icon, Link, Typography } from '@saviynt/design-system';

import { useIspmContext } from '../../../context';
import { useChatOverlayContext } from '../../../context/chatOverlayContext';
import {
  type AskApiContentType,
  type AskApiResponse,
  ErrorTypeEnum,
  type SampleDataAPIResponse,
} from '../../../data-layer/types';
import { Logger } from '../../../services';
import { useInvertedLocalize, useLocalize } from '../../../utils/localization';
import { invertedMessages } from '../../../utils/messages';
import {
  type GeneratedContentType,
  GeneratedContentTypeEnum,
} from '../../savi-content-panels/types';

import './SaviDataResponse.css';

interface SaviDataResponseProps {
  data: AskApiResponse | SampleDataAPIResponse;
}

export default function SaviDataResponse({ data }: SaviDataResponseProps) {
  const { updateChatOverlayState } = useChatOverlayContext();
  const { updateIspmState } = useIspmContext();

  const l = useInvertedLocalize(invertedMessages);
  const localize = useLocalize();

  const handleShowData = () => {
    if ('contentType' in data && !('error' in data)) {
      updateChatOverlayState('showGeneratedContent', true);
      updateChatOverlayState('contentPanelData', data);

      const contentTypeToPanelKindMap: Record<
        AskApiContentType,
        GeneratedContentType | null
      > = {
        sampleData: GeneratedContentTypeEnum.TABLE,
        chart: GeneratedContentTypeEnum.CHART,
        query: GeneratedContentTypeEnum.LOW_CONFIDENCE,
        text: null,
      };

      const panelKind = contentTypeToPanelKindMap[data.contentType];

      if (panelKind) {
        updateChatOverlayState('contentPanelKind', panelKind);
      } else {
        Logger.info('Unknown content type:', data.contentType);
      }
    } else {
      Logger.info('Content type is not present in the data');
    }
  };

  // Handle click of reprocessAttemp
  const handleReprocessAttempt = useCallback(() => {
    if (
      'request' in data &&
      data.request?.freeText &&
      data.request.freeText.length > 0
    ) {
      updateIspmState('userQuery', data.request.freeText);
    }
  }, [data, updateIspmState]);

  const formatStringToReact = (text?: string) => {
    if (!text) {
      return null;
    }

    return text.split('\n\n').map((block) => (
      <Typography
        kind='body2'
        key={block}
        className='Ispm-SaviDataResponse-text'>
        {block.trim()}
      </Typography>
    ));
  };

  const getResponseNode = () => {
    if ('error' in data) {
      if (data.error.type === ErrorTypeEnum.Copilot) {
        return (
          <div className='Ispm-reprocessPrompt'>
            <Typography kind='body2' className='Ispm-reprocessPrompt-text'>
              {l(
                'Sorry, there was an error or interruption while processing your prompt. You can either reprocess your original prompt or try a new one.'
              )}
            </Typography>
            {'request' in data && (
              <Button
                type='button'
                kind='filled'
                size='medium'
                onClick={handleReprocessAttempt}
                leftIcon={
                  <Icon kind='frontIcon' size='medium' color='neutral-100' />
                }>
                {l('Reprocess Request')}
              </Button>
            )}
          </div>
        );
      }

      return (
        <Typography kind='body2' className='Ispm-SaviDataResponse-text'>
          {localize({
            id: data.error.errorCode,
            defaultMessage: data.error.errorMsg,
          })}
        </Typography>
      );
    }

    const formattedElements = formatStringToReact(data.text);

    return (
      <>
        {'text' in data && formattedElements}
        {'links' in data && 'content' in data && data.content.length > 0 && (
          <Link
            data-testid='responseLink'
            text={data.links[0].text}
            color='neutral'
            onClick={handleShowData}
            prefixIconKind='LeftArrow'
            size='small'
          />
        )}
      </>
    );
  };

  return <div className='Ispm-SaviDataResponse'>{getResponseNode()}</div>;
}
